/**
 * Scoring Engine - BVAL CCS Dual Meet Head-to-Head Computation
 * 
 * Scoring rules:
 * - Individual events: 1st = 5, 2nd = 3, 3rd = 1 (in each head-to-head)
 * - Relay events: Winner = 5, Loser = 0
 * - Ties: Split combined points of tied places
 * - Uncontested places: Points go unassigned
 */

const POINTS = [5, 3, 1] // 1st, 2nd, 3rd

/**
 * All 17 events in BVAL order
 */
export const EVENTS = [
  { code: 'LJ', name: 'Long Jump', type: 'individual', order: 1 },
  { code: 'HJ', name: 'High Jump', type: 'individual', order: 2 },
  { code: 'TJ', name: 'Triple Jump', type: 'individual', order: 3 },
  { code: 'PV', name: 'Pole Vault', type: 'individual', order: 4 },
  { code: 'SP', name: 'Shot Put', type: 'individual', order: 5 },
  { code: 'DT', name: 'Discus', type: 'individual', order: 6 },
  { code: '4x1', name: '4×100m Relay', type: 'relay', order: 7 },
  { code: '1600', name: '1600m', type: 'individual', order: 8 },
  { code: 'HH', name: '110m/100m HH', type: 'individual', order: 9, boysName: '110m HH', girlsName: '100m HH' },
  { code: '400', name: '400m', type: 'individual', order: 10 },
  { code: '100', name: '100m', type: 'individual', order: 11 },
  { code: '800', name: '800m', type: 'individual', order: 12 },
  { code: '300IH', name: '300m IH', type: 'individual', order: 13 },
  { code: '200', name: '200m', type: 'individual', order: 14 },
  { code: '4x8', name: '4×800m Relay', type: 'relay', order: 15 },
  { code: '3200', name: '3200m', type: 'individual', order: 16 },
  { code: '4x4', name: '4×400m Relay', type: 'relay', order: 17 },
]

/**
 * Get event display name based on division
 */
export function getEventName(event, division) {
  if (event.code === 'HH') {
    return division === 'varsity_boys' ? event.boysName : event.girlsName
  }
  return event.name
}

/**
 * Generate all unique head-to-head pairings from a list of schools
 * For N schools: N*(N-1)/2 pairings
 */
export function generatePairings(schools) {
  const pairings = []
  for (let i = 0; i < schools.length; i++) {
    for (let j = i + 1; j < schools.length; j++) {
      pairings.push([schools[i], schools[j]])
    }
  }
  return pairings
}

/**
 * Compute head-to-head score for one individual event between two schools
 * 
 * @param {Array} results - All results for this event [{place, school}, ...] sorted by place
 * @param {string} schoolA 
 * @param {string} schoolB
 * @returns {{scoreA: number, scoreB: number}}
 */
export function computeIndividualEvent(results, schoolA, schoolB) {
  // Filter to only runners from these two schools
  const filtered = results
    .filter(r => r.school === schoolA || r.school === schoolB)
    .sort((a, b) => a.place - b.place)

  if (filtered.length === 0) return { scoreA: 0, scoreB: 0 }

  // Group by place to handle ties
  const placeGroups = groupByPlace(filtered)

  // Assign head-to-head positions (re-rank)
  let scoreA = 0
  let scoreB = 0
  let positionIndex = 0 // tracks which scoring position we're at (0=1st, 1=2nd, 2=3rd)

  for (const group of placeGroups) {
    if (positionIndex >= 3) break // Only top 3 positions score

    const count = group.length
    // Collect the points for positions this group occupies
    let totalPoints = 0
    let scoringPositions = 0
    for (let i = 0; i < count && (positionIndex + i) < 3; i++) {
      totalPoints += POINTS[positionIndex + i]
      scoringPositions++
    }

    if (scoringPositions > 0 && count > 1) {
      // Tie: split points equally among tied athletes
      const splitPoints = totalPoints / count
      for (const result of group) {
        if (result.school === schoolA) scoreA += splitPoints
        else if (result.school === schoolB) scoreB += splitPoints
      }
    } else if (count === 1) {
      // Single athlete at this position
      const pts = POINTS[positionIndex] || 0
      if (group[0].school === schoolA) scoreA += pts
      else if (group[0].school === schoolB) scoreB += pts
    }

    positionIndex += count
  }

  return { scoreA, scoreB }
}

/**
 * Compute head-to-head score for a relay event between two schools
 * Winner gets 5, loser gets 0
 * 
 * @param {Array} results - Relay results [{place, school}] 
 * @param {string} schoolA
 * @param {string} schoolB
 * @returns {{scoreA: number, scoreB: number}} 
 */
export function computeRelayEvent(results, schoolA, schoolB) {
  // Filter to only these two schools
  const filtered = results
    .filter(r => r.school === schoolA || r.school === schoolB)
    .sort((a, b) => a.place - b.place)

  if (filtered.length === 0) return { scoreA: 0, scoreB: 0 }

  // Check for tie
  if (filtered.length >= 2 && filtered[0].place === filtered[1].place) {
    // Tie in relay: split 5 points
    const splitPts = 5 / 2
    return {
      scoreA: filtered.some(r => r.school === schoolA) ? splitPts : 0,
      scoreB: filtered.some(r => r.school === schoolB) ? splitPts : 0
    }
  }

  // Winner is first in the filtered+sorted list
  const winner = filtered[0]
  return {
    scoreA: winner.school === schoolA ? 5 : 0,
    scoreB: winner.school === schoolB ? 5 : 0
  }
}

/**
 * Compute all head-to-head scores for an entire meet
 * 
 * @param {Array} schools - List of school names
 * @param {Object} eventResults - { eventId: { eventType, results: [{place, school}] } }
 * @returns {Array} - [{schoolA, schoolB, scoreA, scoreB, breakdown: {eventCode: {scoreA, scoreB}}}]
 */
export function computeMeetScores(schools, eventResults) {
  const pairings = generatePairings(schools)

  return pairings.map(([schoolA, schoolB]) => {
    let totalA = 0
    let totalB = 0
    const breakdown = {}

    for (const [eventId, eventData] of Object.entries(eventResults)) {
      const { eventCode, eventType, results } = eventData
      if (!results || results.length === 0) continue

      let eventScore
      if (eventType === 'relay') {
        eventScore = computeRelayEvent(results, schoolA, schoolB)
      } else {
        eventScore = computeIndividualEvent(results, schoolA, schoolB)
      }

      breakdown[eventCode] = eventScore
      totalA += eventScore.scoreA
      totalB += eventScore.scoreB
    }

    return {
      schoolA,
      schoolB,
      scoreA: totalA,
      scoreB: totalB,
      breakdown
    }
  })
}

/**
 * Group results by place number (for tie detection)
 */
function groupByPlace(results) {
  const groups = []
  let currentGroup = []
  let currentPlace = null

  for (const r of results) {
    if (r.place !== currentPlace) {
      if (currentGroup.length > 0) groups.push(currentGroup)
      currentGroup = [r]
      currentPlace = r.place
    } else {
      currentGroup.push(r)
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup)
  return groups
}
